import classNames from 'classnames';
import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ORANGE, ORANGE_SECONDARY, PRIMARY } from 'constants/colors';

import { getPeriodsInterval } from 'components/shared/roster/RosterViewByDuty/utils';
import { getHoursMinutes } from 'utils/date';
import { brightness } from 'utils/misc';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import { Icon } from '@sdb/planning-ui';
import { Tooltip } from 'components/shared';
import { GRAY, WHITE } from 'constants/colors';

import type { Duty as DutyType } from './types';

function TooltipContent({ duty, portal }: { duty: DutyType; portal: string }) {
  'use memo';

  const { dutyCode, periods, notes = [], qualifications, isPublished } = duty;
  const { t } = useTranslation('common');
  const validNotes = notes.filter(
    ({ text }) => text != null && text.trim() != ''
  );
  const hasNotes = Boolean(validNotes.length);
  const hasQualifications = Boolean(qualifications && qualifications.length);
  // const isNotFromBaseplan = !isFromBasePlan;
  const isNotFromBaseplan = false;
  const isRosterPortal = portal === 'roster';

  return (
    <Table size="small" sx={{ 'tr:last-child td': { border: 0 } }}>
      <TableBody>
        {!isPublished && (
          <TableRow>
            <TableCell colSpan={2}>
              <strong>
                <i>{t('notPublished')}</i>
              </strong>
            </TableCell>
          </TableRow>
        )}
        {dutyCode && (
          <TableRow>
            <TableCell>
              <strong>{t('dutyCode')}</strong>
            </TableCell>
            <TableCell>{dutyCode.code}</TableCell>
          </TableRow>
        )}
        <TableRow>
          <TableCell>
            <strong>{t('periods')}</strong>
          </TableCell>
          <TableCell>
            {periods.map((period) => (
              <div key={period.start + period.end}>
                <div>
                  {getHoursMinutes(period.start)} -{' '}
                  {getHoursMinutes(period.end)}{' '}
                  {period.dutyHourType?.description}
                </div>
              </div>
            ))}
          </TableCell>
        </TableRow>
        {!duty.isOpen && duty.isDutyHourTypeChanged && (
          <TableRow>
            <TableCell style={{ fontSize: 0 }}>
              <Icon type="asterisk" fontSize="small" />
            </TableCell>
            <TableCell>{t('dhtChanged')}</TableCell>
          </TableRow>
        )}
        {isNotFromBaseplan && (
          <TableRow>
            <TableCell style={{ fontSize: 0 }}>
              <Icon type="chevron-up" fontSize="small" />
            </TableCell>
            <TableCell>{t('notFromBaseplan')}</TableCell>
          </TableRow>
        )}
        {duty.isOpen && (
          <>
            {duty.sentToFlex && (
              <TableRow>
                <TableCell style={{ fontSize: 0 }}>
                  <Icon type="arrow-right-arrow-left" fontSize="small" />
                </TableCell>
                <TableCell>{t('isSentToFlex')}</TableCell>
              </TableRow>
            )}
            {duty.isSentForSelfMatch && (
              <TableRow>
                <TableCell style={{ fontSize: 0 }}>
                  <Icon type="users" fontSize="small" />
                </TableCell>
                <TableCell>{t('openForSelfMatch')}</TableCell>
              </TableRow>
            )}
            {Boolean(duty.subscribedEmployees?.length) && (
              <TableRow>
                <TableCell style={{ verticalAlign: 'top' }}>
                  <Icon type="users" fontSize="small" />
                </TableCell>
                <TableCell>
                  <div>
                    <strong>{t('subscribers')}</strong>
                  </div>
                  {duty.subscribedEmployees?.map(
                    ({ nameInformal, id, number }) => (
                      <div key={id || number}>{nameInformal}</div>
                    )
                  )}
                </TableCell>
              </TableRow>
            )}
            {isRosterPortal && Boolean(duty.preferredEmployees?.length) && (
              <TableRow>
                <TableCell>
                  <Icon type="user-crown" fontSize="small" />
                </TableCell>
                <TableCell>
                  <div>
                    <strong>{t('preferredEmployees')}</strong>
                  </div>
                  {duty.preferredEmployees?.map(
                    ({ nameInformal, id, number }) => (
                      <div key={id || number}>{nameInformal}</div>
                    )
                  )}
                </TableCell>
              </TableRow>
            )}
          </>
        )}
        {hasQualifications && (
          <TableRow>
            <TableCell style={{ fontSize: 0 }}>
              <Icon type="file-certificate" fontSize="small" />
            </TableCell>
            <TableCell>
              <div>
                <strong>{t('qualifications')}</strong>
              </div>
              {qualifications?.map(({ name, id }) => (
                <div key={id}>{name}</div>
              ))}
            </TableCell>
          </TableRow>
        )}
        {hasNotes && (
          <TableRow>
            <TableCell style={{ fontSize: 0 }}>
              <Icon type="comment" fontSize="small" />
            </TableCell>
            <TableCell>
              <div>
                <strong>{t('notes')}</strong>
              </div>
              {validNotes.map(({ text, createdOn }, i) => (
                <div key={text + (createdOn || i)}>{text.trim()}</div>
              ))}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

const DutyStyles = styled.div`
  cursor: default;
  height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  line-height: 14px;
  margin-bottom: 2px;
  position: relative;
  border-radius: 3px;
  padding-bottom: 2px;
  border: 1px solid;

  &:not(.is-open) {
    border-color: ${PRIMARY};
    background-color: ${brightness(PRIMARY, 85)};
  }

  &.is-open {
    border-color: ${ORANGE_SECONDARY};
    background-color: ${brightness(ORANGE, 85)};
  }

  &.is-faded {
    opacity: 0.5;
  }

  &.has-color {
    .duty-code {
      display: inline-block;
      max-width: calc(100% - 6px);
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }

  .has-subscribers {
    position: relative;

    .cnt {
      position: absolute;
      right: -1px;
      top: 4px;
      transform: translate(50%, -50%);
      font-size: 12px;
      line-height: 1;
      font-size: 10px;
    }
  }

  .top-right-icons {
    position: absolute;
    right: 2px;
    top: 2px;
    line-height: 1;
    display: flex;

    svg {
      width: 8px;
      height: 8px;

      &.dht-changed {
        width: 7px;
        height: 7px;
      }

      & + svg {
        margin-left: 2px;
      }
    }
  }

  .bottom-icon {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    .duty-icon {
      margin: 2px;
    }

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .duty-icon {
    font-size: 10px;

    &.subscribers {
      font-size: 11px;
    }
  }
`;

type Props = {
  duty: DutyType;
  portal: string;
};

export default function Duty({ duty, portal }: Props) {
  'use memo';

  const { rosterDisplayDutiesByCode, rosterDutyDisplayMode } = useSelector(
    (state) => state.preferences
  );
  const { t } = useTranslation('common');
  const { dutyCode, periods, notes = [], qualifications, isPublished } = duty;
  // const isDutyHourTypeChanged = !duty.isOpen && duty.isDutyHourTypeChanged;
  const isDutyHourTypeChanged = !duty.isOpen && false;
  const periodsInterval = getPeriodsInterval(periods);
  const { code, colorCode } = dutyCode || {};
  const timesLabel = (
    <>
      <div>{getHoursMinutes(periodsInterval.startTime)}</div>
      <div>{getHoursMinutes(periodsInterval.endTime)}</div>
    </>
  );
  const validNotes = notes.filter(
    ({ text }) => text != null && text.trim() != ''
  );
  const hasNotes = Boolean(validNotes.length);
  const hasQualifications = Boolean(qualifications && qualifications.length);
  // const isNotFromBaseplan = !isFromBasePlan;
  const isNotFromBaseplan = false;
  const isRosterPortal = portal === 'roster';
  const hasSubscribers = Boolean(
    duty.isOpen && duty.subscribedEmployees?.length
  );
  const hasPreferredEmployees =
    isRosterPortal && Boolean(duty.isOpen && duty.preferredEmployees?.length);
  const showSentToFlex = duty.isOpen && duty.sentToFlex;
  const isSentForSelfMatch = duty.isOpen && duty.isSentForSelfMatch;

  return (
    <Tooltip
      title={
        !duty.isFromDifferentCostCenter ? (
          <TooltipContent duty={duty} portal={portal} />
        ) : (
          t('dutyFromDifferentPlanUnit')
        )
      }
      arrow
      enterDelay={300}
      enterNextDelay={300}
      placement="right"
      disableInteractive
    >
      <DutyStyles
        className={classNames('duty', {
          'has-color': Boolean(colorCode),
          'is-open': duty.isOpen,
          'is-faded': !isPublished
        })}
        style={
          colorCode || duty.isFromDifferentCostCenter
            ? {
                borderColor: duty.isFromDifferentCostCenter ? GRAY : colorCode,
                backgroundColor: duty.isFromDifferentCostCenter
                  ? brightness(WHITE as `#${string}`, 85)
                  : brightness(colorCode as `#${string}`, 85)
              }
            : undefined
        }
      >
        <div className="top-right-icons">
          {isDutyHourTypeChanged && (
            <Icon
              type="asterisk"
              fontSize="small"
              className="duty-icon dht-changed"
            />
          )}
          {isNotFromBaseplan && (
            <Icon
              type="chevron-up"
              fontSize="small"
              className="duty-icon not-from-baseplan"
            />
          )}
        </div>
        {(() => {
          const effectiveMode =
            rosterDutyDisplayMode ??
            (rosterDisplayDutiesByCode
              ? 'DUTY_DISPLAY_CODES_TIMES_ON_CHANGE'
              : 'DUTY_DISPLAY_START_END_TIME');

          if (duty.isFromDifferentCostCenter) {
            return (
              <div className="code-label" style={{ fontStyle: 'italic' }}>
                {timesLabel}
              </div>
            );
          }

          if (effectiveMode === 'DUTY_DISPLAY_START_END_TIME') {
            return <div className="code-label">{timesLabel}</div>;
          }

          if (effectiveMode === 'DUTY_DISPLAY_ALWAYS_BOTH' && code) {
            return (
              <div className="duty-code code-label">
                <div>{code}</div>
                {timesLabel}
              </div>
            );
          }

          // CODES_* modes: show code only
          return code ? (
            <div className="duty-code code-label">{code}</div>
          ) : (
            <div className="code-label">{timesLabel}</div>
          );
        })()}
        <div className="bottom-icon">
          {hasNotes && <Icon type="comment" className="duty-icon notes" />}
          {duty.isOpen && (isSentForSelfMatch || hasSubscribers) && (
            <span className={classNames({ 'has-subscribers': hasSubscribers })}>
              <Icon
                type="users"
                fontSize="small"
                className="duty-icon subscribers"
              />
              {hasSubscribers && (
                <span className="cnt">{duty.subscribedEmployees?.length}</span>
              )}
            </span>
          )}
          {showSentToFlex && (
            <Icon
              type="arrow-right-arrow-left"
              fontSize="small"
              className="duty-icon sent-to-flex"
            />
          )}
          {hasPreferredEmployees && (
            <Icon
              type="user-crown"
              fontSize="small"
              className="duty-icon pref-employees"
            />
          )}
          {hasQualifications && (
            <Icon
              type="file-certificate"
              fontSize="small"
              className="duty-icon qualifications"
            />
          )}
        </div>
      </DutyStyles>
    </Tooltip>
  );
}
